import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DreamerExperimentArtifact } from "~/api/reasoningTypes";
import type { Run } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import type { ActionSpec } from "./usePendingAction";

type Metric = "completion" | "failed-steps" | "duration-ms";

/** Test a Dreamer proposal as a candidate flow version, measure it, publish or roll back. */
export function ExperimentSection({
  run,
  experiments,
  busy,
  act,
}: {
  run: Run;
  experiments: DreamerExperimentArtifact[];
  busy: boolean;
  act: (action: ActionSpec) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [candidateFlowSlug, setCandidateFlowSlug] = useState("");
  const [candidateVersion, setCandidateVersion] = useState("");
  const [hypothesis, setHypothesis] = useState("");
  const [metric, setMetric] = useState<Metric>("completion");
  const [candidateRunId, setCandidateRunId] = useState("");
  const plans = experiments.filter((artifact) => artifact.path.endsWith("/plan.json"));
  return (
    <section className="flex flex-col gap-2 rounded border border-divider p-3">
      <h5 className="font-semibold">{t("Test a Dreamer proposal")}</h5>
      <p>
        {t(
          "Create a candidate version in a separate flow, run it, then record the measured outcome here. Publishing copies that tested definition into the original flow as a new version.",
        )}
      </p>
      <input
        aria-label={t("Candidate flow slug")}
        placeholder={t("Candidate flow slug")}
        value={candidateFlowSlug}
        onChange={(e) => setCandidateFlowSlug(e.target.value)}
      />
      <input
        aria-label={t("Candidate flow version")}
        type="number"
        min="1"
        placeholder={t("Candidate version")}
        value={candidateVersion}
        onChange={(e) => setCandidateVersion(e.target.value)}
      />
      <textarea
        aria-label={t("Experiment hypothesis")}
        placeholder={t("What should improve, and why?")}
        value={hypothesis}
        onChange={(e) => setHypothesis(e.target.value)}
      />
      <select
        aria-label={t("Experiment metric")}
        value={metric}
        onChange={(e) => setMetric(e.target.value as Metric)}
      >
        <option value="completion">{t("Successful completion")}</option>
        <option value="failed-steps">{t("Fewer failed steps")}</option>
        <option value="duration-ms">{t("Shorter duration")}</option>
      </select>
      <Button
        disabled={
          busy ||
          !candidateFlowSlug.trim() ||
          !Number.isInteger(Number(candidateVersion)) ||
          Number(candidateVersion) < 1 ||
          !hypothesis.trim()
        }
        onClick={() =>
          void act({
            kind: "experiment-plan",
            input: {
              runId: run.id,
              candidateFlowSlug: candidateFlowSlug.trim(),
              candidateVersion: Number(candidateVersion),
              hypothesis: hypothesis.trim(),
              metric,
            },
          })
        }
      >
        {t("Plan experiment")}
      </Button>
      {plans.map((plan) => {
        const experimentId = plan.path.split("/")[2] ?? "";
        const stage = (name: string) =>
          experiments.find(
            (artifact) => artifact.path === `reasoning/experiments/${experimentId}/${name}.json`,
          );
        const measurement = stage("measure")?.content;
        const publication = stage("publish")?.content;
        const rollback = stage("rollback")?.content;
        return (
          <article
            key={plan.path}
            className="flex flex-col gap-2 rounded border border-divider p-3"
          >
            <p>
              {String(plan.content?.hypothesis ?? t("Experiment"))} ·{" "}
              {String(plan.content?.metric ?? "")}
            </p>
            <p>
              {t("Candidate: {{slug}} v{{version}}", {
                slug: String(plan.content?.candidateSlug ?? ""),
                version: String(plan.content?.candidateVersion ?? ""),
              })}
            </p>
            {measurement ? (
              <p>
                {t(
                  "Measured baseline {{baseline}} → candidate {{candidate}}. Improved: {{improved}}",
                  {
                    baseline: String(measurement.baselineValue),
                    candidate: String(measurement.candidateValue),
                    improved: String(measurement.improved),
                  },
                )}
              </p>
            ) : (
              <>
                <input
                  aria-label={t("Candidate run ID for {{experiment}}", {
                    experiment: experimentId,
                  })}
                  placeholder={t("Completed candidate run ID")}
                  value={candidateRunId}
                  onChange={(e) => setCandidateRunId(e.target.value)}
                />
                <Button
                  disabled={busy || !candidateRunId.trim()}
                  onClick={() =>
                    void act({
                      kind: "experiment-measure",
                      input: { runId: run.id, experimentId, candidateRunId: candidateRunId.trim() },
                    })
                  }
                >
                  {t("Measure candidate run")}
                </Button>
              </>
            )}
            {measurement?.improved === true && !publication && (
              <Button
                disabled={busy}
                onClick={() =>
                  void act({ kind: "experiment-publish", input: { runId: run.id, experimentId } })
                }
              >
                {t("Publish measured version")}
              </Button>
            )}
            {publication && (
              <p>
                {t("Published original flow version {{version}}.", {
                  version: String(publication.version),
                })}
              </p>
            )}
            {publication && !rollback && (
              <Button
                disabled={busy}
                onClick={() =>
                  void act({ kind: "experiment-rollback", input: { runId: run.id, experimentId } })
                }
              >
                {t("Roll back to baseline as a new version")}
              </Button>
            )}
            {rollback && (
              <p>
                {t("Rolled back in version {{version}}.", { version: String(rollback.version) })}
              </p>
            )}
          </article>
        );
      })}
    </section>
  );
}
